import { makeRef } from "@/components/composables/reactivityTypes";
import type { ControlIn, IControl } from "@/controls/control";
import { Control, extendIn, mergeIn, WrappedControl } from "@/controls/control";
import type { ShallowReactive } from "vue";
import { computed, watch } from "vue";
import type { CreateElement } from "vue/types/umd";
import { VCol, VContainer, VRow, VDivider } from "vuetify/lib";
import BmCard from "@/components/controls/card/BmCard.vue";

type LayoutIn = ControlIn & {};

abstract class LayoutBase extends Control {
  #in: ShallowReactive<LayoutIn>;

  private children = makeRef<readonly IControl[]>([]);

  get in() {
    return this.#in;
  }

  constructor() {
    super();
    this.#in = extendIn(super.in, {});
  }

  protected visibleChildren = computed(() =>
    this.children.value.filter((x) => x.in.visible)
  );

  addChildren(childrenToAdd: readonly IControl[]) {
    this.children.value = this.children.value.concat(childrenToAdd);
    for (const child of childrenToAdd) {
      child.parent = this;
    }
  }

  allChildren() {
    return this.children.value;
  }

  clearChildren() {
    for (const child of this.children.value) {
      child.parent = null;
    }
    this.children.value = [];
  }

  setChildren(children: readonly IControl[]) {
    this.clearChildren();
    this.addChildren(children);
  }
}

export class FlowStrategy {
  constructor(
    private _xs: number,
    private _sm: number,
    private _md: number,
    private _lg: number,
    private _xl: number
  ) {}
  get xs() {
    return this._xs;
  }
  get sm() {
    return this._sm;
  }

  get md() {
    return this._md;
  }

  get lg() {
    return this._lg;
  }

  get xl() {
    return this._xl;
  }

  static SingleColumn = new FlowStrategy(12, 12, 12, 12, 12);
  static TwoColumnsOnLargeScreen = new FlowStrategy(12, 12, 6, 6, 4);
  static ThreeColumnsOnLargeScreen = new FlowStrategy(12, 12, 6, 4, 3);
  static FourColumnsOnLargeScreen = new FlowStrategy(12, 6, 4, 3, 2);
}

type FlowLayoutIn = LayoutIn & {
  flowStrategy: FlowStrategy;
  rowContentVerticalAlignment: "none" | "center";
};

type ControlFlowOverride = {
  control: IControl;
  strategy: FlowStrategy;
};

export class FlowLayout extends LayoutBase {
  #in: ShallowReactive<FlowLayoutIn>;

  private _controlFlowOverrides = makeRef<readonly ControlFlowOverride[]>([]);

  get in() {
    return this.#in;
  }

  constructor() {
    super();
    this.#in = extendIn(super.in, {
      flowStrategy: FlowStrategy.FourColumnsOnLargeScreen,
      rowContentVerticalAlignment: "none" as const,
    });
  }

  render(h: CreateElement) {
    const childNodes = this.visibleChildren.value.map((child) => {
      const strategy =
        this._controlFlowOverrides.value.find((x) => x.control === child)
          ?.strategy ?? this.in.flowStrategy;
      return h(
        VCol,
        {
          props: {
            cols: strategy.xs,
            sm: strategy.sm,
            md: strategy.md,
            lg: strategy.lg,
            xl: strategy.xl,
          },
        },
        [
          h(WrappedControl, {
            props: {
              control: child,
            },
          }),
        ]
      );
    });

    const rowClass =
      this.in.rowContentVerticalAlignment === "center"
        ? "align-center"
        : undefined;

    const row = h(VRow, { props: {}, class: rowClass }, [childNodes]);

    return h(VContainer, { class: "pa-0", props: { fluid: true } }, [row]);
  }

  overrideStrategy(control: IControl, strategy: FlowStrategy) {
    this._controlFlowOverrides.value = this._controlFlowOverrides.value
      .filter((x) => x.control !== control)
      .concat([{ control, strategy }]);
  }
}

class VStack extends LayoutBase {
  constructor() {
    super();
  }

  render(h: CreateElement) {
    const rows = this.visibleChildren.value.map((child) =>
      h("div", [child.render(h)])
    );

    return h("div", rows);
  }
}

class Span extends LayoutBase {
  constructor() {
    super();
  }

  render(h: CreateElement) {
    return h(
      "span",
      this.visibleChildren.value.map((child) => child.render(h))
    );
  }
}

// Oringal VStack implementation - used default Veutify container spacing
// but not really what we want from a vstack default.
// Change uses of this to just vstack, and remove once obsolete
/**
 * @deprecated Use VStack instead
 */
export class VStackSpaced extends LayoutBase {
  constructor() {
    super();
  }

  render(h: CreateElement) {
    const rows = this.visibleChildren.value.map((child) =>
      h(VRow, { props: {} }, [
        h(
          VCol,
          {
            props: {
              cols: 12,
            },
          },
          [child.render(h)]
        ),
      ])
    );

    return h(VContainer, { class: "pa-0", props: { fluid: true } }, rows);
  }
}

export class FluidSpacedButtons extends LayoutBase {
  constructor() {
    super();
  }

  render(h: CreateElement) {
    const childControls = this.visibleChildren.value.map((child) =>
      h("span", { class: "pr-2" }, [child.render(h)])
    );
    return h(
      VContainer,
      { class: "pa-0", props: { fluid: true } },
      childControls
    );
  }
}

type DividerIn = ControlIn & {};

export class Divider extends Control {
  #in: ShallowReactive<DividerIn>;

  get in() {
    return this.#in;
  }

  constructor() {
    super();
    this.#in = extendIn(super.in, {});
  }

  render(h: CreateElement) {
    return h(VDivider, { props: {} });
  }
}

type SpaceOccupierIn = ControlIn & {
  child: IControl | null;
};

export class SpaceOccupier extends Control {
  #in: ShallowReactive<SpaceOccupierIn>;

  get in() {
    return this.#in;
  }

  constructor() {
    super();
    this.#in = extendIn(super.in, { child: null });
  }

  render(h: CreateElement) {
    return this.in.child?.in.visible ? this.in.child.render(h) : h("span");
  }
}

type CardIn = ControlIn & {
  title: string;
  content: IControl | null;
  width: number | undefined;
  height: number | undefined;
};

class Card extends Control {
  #in: ShallowReactive<CardIn>;

  get in() {
    return this.#in;
  }

  constructor() {
    super();
    this.#in = extendIn(super.in, {
      title: "",
      content: null,
      width: undefined,
      height: undefined,
    });

    watch(
      () => this.in.content,
      () => {
        if (this.in.content) {
          this.in.content.parent = this;
        }
      }
    );
  }

  render(h: CreateElement) {
    return h(BmCard, {
      props: {
        label: this.in.title,
        height: this.in.height,
        width: this.in.width,
      },
      scopedSlots: {
        default: () => this.in.content?.render(h),
      },
    });
  }
}

type HBarIn = ControlIn & {
  height: number;
  colour: string;
};

class HBar extends Control {
  #in: ShallowReactive<HBarIn>;

  get in() {
    return this.#in;
  }

  constructor() {
    super();
    this.#in = extendIn(super.in, {
      height: 0,
      colour: "#000000",
    });
  }

  render(h: CreateElement) {
    return h("div", {
      style:
        "width: 100%; background-color: " +
        this.in.colour +
        "; height: " +
        this.in.height +
        "px;",
    });
  }
}

function mergeWithContents<TCtrl extends LayoutBase>(
  ctrl: TCtrl,
  opts?: {},
  contents?: readonly IControl[]
) {
  mergeIn(ctrl, opts);
  if (contents) {
    ctrl.addChildren(contents);
  }
  return ctrl;
}

const layouts = {
  card: (opts: Partial<CardIn>) => mergeIn(new Card(), opts),
  vStack: (opts?: Partial<LayoutIn>) => mergeIn(new VStack(), opts),
  span: (opts?: Partial<LayoutIn>, contents?: readonly IControl[]) =>
    mergeWithContents(new Span(), opts, contents),
  /**
   * @deprecated Use VStack instead
   */
  vStackSpaced: (opts?: Partial<LayoutIn>) => mergeIn(new VStackSpaced(), opts),
  flowLayout: (opts?: Partial<FlowLayoutIn>) =>
    mergeIn(new FlowLayout(), opts ?? {}),
  divider: (opts?: Partial<DividerIn>) => mergeIn(new Divider(), opts ?? {}),
  fluidSpacedButtons: () => new FluidSpacedButtons(),
  spaceOccupier: (opts?: Partial<SpaceOccupierIn>) =>
    mergeIn(new SpaceOccupier(), opts ?? {}),
  hBar: (opts: Partial<HBarIn>) => mergeIn(new HBar(), opts),
};

export { layouts };
